


//Api Helper functions for Stock Transfer Module

import { getRequest, postRequest, putRequest } from '../../../services/httpService';
import * as Yup from 'yup';
import { Clinic, Department, BatchInfo } from '../Common/inventory.helper';

// ------------------- main tab styles -------------------

export function getTabSx(selectedTab: number, index: number) {
  return {
    marginTop: '10px',
    color: selectedTab === index ? '#E17E61' : '#CFD1D4',
    fontWeight: selectedTab === index ? 600 : 500,
    borderBottom: `2px solid ${selectedTab === index ? '#E17E61' : '#CFD1D4'}`,
    width: '33.33%',
    fontFamily: 'Montserrat',
  };
}

export const tableHeaderCellBaseSx = {
  backgroundColor: '#FAFAFA',
  whiteSpace: 'nowrap',
  border: 'none',
  padding: '6px 6px',
};

export const tableContainerSx = {
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#a8a8a8',
    },
  },                       
};

export const tableCellBaseSx = {
  padding: '9px 6px', // reduced row cell padding
  verticalAlign: 'middle',
};

export const tabButtonStyles = {
  base: {
    backgroundColor: 'rgba(250, 250, 250, 1)',
    fontFamily: 'Montserrat',
    fontSize: '16px',
    color: 'rgba(193, 193, 193, 1)',
    textTransform: 'capitalize',
    padding: '0px 45px',
    '&:hover': {
      color: '#E17E61 !important',
      fontWeight: 500,
      backgroundColor: '#FFFFFF',
      boxShadow: '1px 1px 4px 0px rgba(0, 0, 0, 0.2)',
      borderRadius: '10px',
      marginRight: '2px',
    },
  },
  selected: {
    backgroundColor: '#FFFFFF',
    boxShadow: '2px 2px 8px 2px rgba(225, 126, 97, 0.6)',
    fontWeight: 600,
    borderRadius: '10px',
    color: '#E17E61',
    padding: '0px 45px',
  },
};

export const statusStyles: Record<StatusType, { color: string; bg: string }> = {
  Approved: { color: '#3FAE49', bg: '#E9F7ED' },
  Finalized: { color: '#1A73E8', bg: '#E9F2FF' },
  Draft: { color: '#9E9E9E', bg: '#F5F5F5' },
  Rejected: { color: '#E53935', bg: '#FFEAEA' },
  PR: { color: '#F4B400', bg: '#FFF7D9' },
  Pending: { color: '#FB8C00', bg: '#FFF2DD' },
  Cancelled: { color: '#D32F2F', bg: '#FFE8E8' },
  Completed: { color: '#43A047', bg: '#E9F7ED' },
};

export const tabSx = {
  '& .MuiTabs-indicator': { display: 'none' },
};

export const autocompleteInputPaddingSx = {
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
    padding: '4.5px 2px 5.5px 3px',
  },
};


export interface ItemRow {
  item_id: number;
  itemCode: string;
  itemName: string;
  available_qty?: number;
  requestToClinicId: number | null;
  requestToStoreId: number | null;
  qty: number;
  qtyUnit: string;
  batches?: BatchInfo[];
  total_base_cost_price?: number;
  total_mrp_cost_price?: number;
  available_stock_uom?: string;
  base_uom_code?: string;
   base_uom_name?: string;
  uom_label?: string;
  uom_conversions?: Array<{
    id?: number;
    item_id?: number;
    uom_id?: number;
    uom_code?: string;
    code?: string;
    uom_static_field?: string;
    conversion_factor?: number;
    converted_mrp?: number;
    converted_cost_price?: number;
  }>;
  departmentOptions?: DepartmentApiObject[];
}
export interface addIndentInitialValues {
  clinic_id: number | null;
  store_id: number | null;
  items: ItemRow[];
  expectedDate: string;
  search: string;
  patient_id: number | null;
  patient: Patient | null;
  againstPatient: boolean;
  remark: string;
  status?: string;
  issueDate?: string;
}

export const addIndentInitialValue: addIndentInitialValues = {
  clinic_id: null,
  store_id: null,
  expectedDate: '',
  search: '',
  patient_id: null,
  patient: null,
  againstPatient: false,
  remark: '',
  status: '',
  items: [],
  issueDate: '',
};

// Yup Validation Schema for Add New Indent
export function createAddIndentValidationSchema() {
  return Yup.object().shape({
    clinic_id: Yup.number().nullable(),
    store_id: Yup.number().nullable(),
    expectedDate: Yup.string().required('Expected Delivery Date is required'),
    remark: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object().shape({
          available_qty: Yup.number().nullable(),
          requestToClinicId: Yup.number().nullable(),
          requestToStoreId: Yup.number()
            .required('Request To is required'),
          qty: Yup.number()
            .typeError('Quantity is required')
            .moreThan(0, 'Quantity should not be zero')
            .required('Quantity is required'),
          qtyUnit: Yup.string().required('UOM is required'),
        })
      )
      .min(1, 'Please add at least one item')
      .required('Items are required'),
  });
}
export interface IndentListApiResponse {
  objects: IndentListResponse[];
  status_counts: StatusCounts;
  meta?: {
    total_count?: number;
    limit?: number;
    offset?: number;
  };
}

export interface IndentItem {
  id: number;
  item_id?: number;
  code: string;
  cost_price: string;
  is_active: boolean;
  is_perishable: boolean;
  mrp: string;
  name: string;
  name_label?: string;
  brand_name: string;
  is_free: boolean;
  is_stimulation_drug: boolean;
  is_trigger_drug: boolean;
  category: string;
  group: string;
  uom: string;
  uom_conversions?: Array<{
    id?: number;
    item_id?: number;
    uom_id?: number;
    uom_code?: string;
    code?: string;
    uom_static_field?: string;
    conversion_factor?: number;
    converted_mrp?: number;
    converted_cost_price?: number;
  }>;
  
}

export interface IndentDetail {
  id: number;
  item: IndentItem;
  indent_qty: number;
  indent_pending_qty: number;
  available_qty: number;
  uom: string;
  uom_label?: string;
  base_uom_label?: string;
  indent_qty_in_base?: number;
  indent_pending_qty_in_base?: number;
}
export interface StatusCounts {
  DRAFT: number;
  PENDING: number;
  FINALIZED: number;
  APPROVED: number;
  CANCELLED: number;
  COMPLETED: number;
  FORWARDED: number;
  FORCE_CLOSED: number;
  PR: number;
  REJECTED: number;
  ALL: number;
  PARTIAL_ISSUED?: number;
  PATIENT?: number;
  STORE?: number;
  DIRECT?: number;
  ISSUED?: number;
  total_count?: number;
  INDENT?: number;
  APPROVAL_PENDING?: number;
  FORE_CLOSED?: number;
}

export interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  middle_name?: string;
  medical_record_no: string;
  gender: string;
  age: number;
}

export interface IndentListResponse {
  id: number;
  from_clinic: string;
  from_clinic_label?: string;
  from_store: string;
  from_store_id?: number;
  from_store_label?: string;
  to_clinic: string;
  to_clinic_label?: string;
  to_store: string;
  to_store_label?: string;
  expected_date: string;
  indent_details?: IndentDetail[];
  patient: Patient | null;
  status: StatusType;
  remark: string | null;
  created_on: string;
  cancel_remark: string | null;
  indent_no: string;
  issue_status: string;
  total_amount: string;
  stock_availability: string;
  forceclose?: boolean;
  pr_no: string | null;
  pr_date: string | null;
  po_numbers: string[];
  po_dates: string[];
  grn_numbers: string[];
  grn_dates: string[];
  issue_numbers: string[];
  issue_dates: string[];
  receive_numbers: string[];
  receive_dates: string[];
  message?: string;
  rejected_remark?: string;
  force_close_remark?: string;
  approved_by?: string;
  created_by?: string;
}

type CreateIndentApiResponse = {
  message: string;
  count: number;
  data: IndentListResponse[];
};

export interface IndentDetailPayload {
  item: number;
  available_qty: number;
  indent_qty: number;
  uom: string | number;
  to_clinic: number | null;
  to_store: number | null;
}

export interface CreateMultipleIndentPayload {
  from_clinic: number | null;
  from_store: number | null;
  expected_date: string;
  patient: number | null;
  remark: string | null;
  status?: string;
  indent_details: IndentDetailPayload[];
}


export interface LocalUserClinics {
  clinic_id?: number;
  clinic__name?: string;
  clinic_name?: string;
  id?: number;
  code?: string;
}

export interface LocalUserDepartments {
  department_id?: number;
  department_name?: string;
  id?: number;
  department__name?: string;
}

export interface UserClinicDepartments {
  clinics?: LocalUserClinics[];
  clinic_department?: Record<string, LocalUserDepartments[]>;
}

// Map a user object (from localStorage) into application `Clinic[]` shape
export function mapUserClinicsResponse(user: UserClinicDepartments | null | undefined): Clinic[] {
  if (!user) return [];
  const clinicsFromUser = Array.isArray(user.clinics) ? (user.clinics as LocalUserClinics[]) : [];
  const clinicDept = user.clinic_department || {};

  const mapped: Clinic[] = clinicsFromUser
    .map((c) => {
      const clinicId = Number(c.clinic_id ?? c.id ?? 0);
      const clinicName = c.clinic__name ?? c.clinic_name ?? '';
      const departmentsRaw = clinicDept?.[String(clinicId)] || clinicDept?.[clinicId] || [];

      const departments: Department[] = Array.isArray(departmentsRaw)
        ? departmentsRaw.map((d) => ({
          id: Number(d.department_id ?? d.id ?? 0),
          name: d.department_name ?? d.department__name ?? '',
          name_label: d.department_name ?? d.department__name ?? '',
        }))
        : [];

      return {
        id: clinicId,
        name: clinicName,
        code: c.code ?? '',
        departments,
      } as Clinic;
    })
    .filter((x) => Boolean(x && x.id));

  return mapped;
}

export interface ApproveIndentsPayload {
  ids: number[];
}
export interface CancelIndentsPayload {
  ids: number[];
}
export interface ApproveIndentsResponse {
  message: string;
  // add other properties if needed
}
export interface RejectIndentsPayload {
  ids: number[];
  remark: string;
}

export interface ForwardIndentsPayload {
  to_store: number;
  to_clinic: number;
}

export interface ForceCloseIndentsPayload {
  remark: string;
}

export interface MultipleForceCloseIndentsPayload {
  ids: number[];
  remark: string;
}
export interface ForceCloseIndentsResponse {
  message: string;
  // add other properties if needed
}
export interface MultipleConvertToPRIndentsPayload {
  ids: number[];
}

// Department API interfaces
export interface DepartmentApiObject {
  id: number;
  name: string;
  clinic__id: number;
  clinic__name: string;
  clinic__name_label?: string;
  name_label: string;
  tenant__name: string;
  is_clinical: boolean;
  available_stock?: number;
  is_linked?: boolean;
  base_uom?: {
    id: number;
    name: string;
    name_label: string;
    code: string;
  };
}

export interface RejectIndentResponse {
  message: string;
}


export interface DepartmentsApiResponse {
  objects: DepartmentApiObject[];
}

export type StatusType =
  | 'Approved'
  | 'Finalized'
  | 'Draft'
  | 'Rejected'
  | 'PR'
  | 'Pending'
  | 'Cancelled'
  | 'Completed';


export interface GetIndentListParams {
  id?: number;
  status?: string;
  limit?: number;
  offset?: number;
  search?: string;
  order_by?: string;
  from_date?: string;
  to_date?: string;
  from_clinic_id?: number;
  from_store_id?: number;
  to_clinic_id?: number;
  to_store_id?: number;
  // Additional params for Issue/Receive prospective lists
  to_clinic?: string;
  receiving_clinic?: string;
  issue_status?: string;
  receive_status?: string;
  created_on_after?: string;
  created_on_before?: string;
  from_store?: string | number;
  to_store?: string | number;
}

// get Indent List API Call


export function getIndentList(params: GetIndentListParams = {}): Promise<IndentListApiResponse> {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const url = `indents/?${query.toString()}`;
  return getRequest(url, { responseType: 'json' });
}

// Create  Indent API Call
export function createIndent(data: FormData): Promise<IndentListResponse> {
  const url = 'indents/';
  return postRequest(url, data, { responseType: 'json' });
}

// Approve Indents API Call
export function ApproveIndents(data: ApproveIndentsPayload): Promise<ApproveIndentsResponse> {
  const url = 'indents/approve/';
  return postRequest(url, data, { responseType: 'json' });
}

// Reject Indents API Call
export function RejectIndents(data: RejectIndentsPayload): Promise<RejectIndentResponse> {
  const url = 'indents/reject/';
  return postRequest(url, data, { responseType: 'json' });
}

// Cancel Indents API Call
export function CancelIndents(data: CancelIndentsPayload): Promise<RejectIndentResponse> {
  const url = 'indents/cancel/';
  return postRequest(url, data, { responseType: 'json' });
}

// Forward Indents API Call
export function forwardIndents(id: number, data: ForwardIndentsPayload): Promise<ForceCloseIndentsResponse> {
  const url = `indents/${id}/forward/`;
  return postRequest(url, data, { responseType: 'json' });
}

// Single Force Close Indents API Call
export function forceCloseIndents(id: number, data: ForceCloseIndentsPayload): Promise<ForceCloseIndentsResponse> {
  const url = `indents/${id}/force_close/`;
  return postRequest(url, data, { responseType: 'json' });
}

// Multiple Force Close Indents API Call
export function MultipleForceCloseIndents(data: MultipleForceCloseIndentsPayload): Promise<ForceCloseIndentsResponse> {
  const url = 'indents/bulk-force-close/';
  return postRequest(url, data, { responseType: 'json' });
}

// Multiple convert To PR API Call
export function MultipleConvertToPRIndents(data: MultipleConvertToPRIndentsPayload): Promise<ForceCloseIndentsResponse> {
  const url = 'indents/bulk-convert-to-pr/';
  return postRequest(url, data, { responseType: 'json' });
}

// get Departments list API Call
export function getDepartmentsList(): Promise<DepartmentsApiResponse> {
  const url = 'departments/';
  return getRequest(url);
}

export function getActiveDepartmentsList(ItemId: number): Promise<DepartmentsApiResponse> {
  const url = `departments/active-departments/?item_id=${ItemId}`;
  return getRequest(url);
}

//create multiple indent API Call
export function createMultipleIndent(data: CreateMultipleIndentPayload): Promise<CreateIndentApiResponse> {
  const url = 'indents/create-multiple/';
  return postRequest(url, data, { responseType: 'json' });
}

// Get Indents by IDs API Call
export function getIndentsByIds(id: number): Promise<IndentListApiResponse> {
  const url = `indents/${id}`;
  return getRequest(url);
}

// Put Indent API Call
export function UpdateIndent(id: number, data: CreateMultipleIndentPayload): Promise<IndentListResponse> {
  const url = `indents/${id}/`;
  return putRequest(url, data, { responseType: 'json' });
}